//#region types
import { MoreActionMenuItem } from "models/propTypes/menuPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, SalaryDeviationConstants } from "utils/constants";
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { DialogEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import useCreateAllowanceDialog from "../forms/useCreateAllowanceDialog";
import useCreateLeaveDialog from "../forms/useCreateLeaveDialog";
import { useSalaryDeviationsService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function useSalaryDeviationHoverMoreActions(
  reloadList?: () => void,
  yearMonth?: number,
  deviationMonth?: ApiModel.SalaryDeviationMonth
) {
  const salaryDeviationsService = useSalaryDeviationsService();
  const createLeaveDialog = useCreateLeaveDialog(reloadList);
  const createAllowanceDialog = useCreateAllowanceDialog(reloadList);

  const onDelete = (salaryDeviationId: number) => {
    return salaryDeviationsService.deleteSalaryDeviation(salaryDeviationId);
  };

  const addAllowanceAction = (personId?: number, personName?: string, rowYearMonth?: number): MoreActionMenuItem => ({
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AddCompensation),
    icon: IconDefinitionConstants.WMoney,
    actionDialog: createAllowanceDialog({ yearMonth: rowYearMonth ?? yearMonth, personId, personName }),
  });

  const addLeaveAction = (personId?: number, personName?: string, rowYearMonth?: number): MoreActionMenuItem => ({
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AddTimeDeviation),
    icon: IconDefinitionConstants.WCalendar,
    actionDialog: createLeaveDialog({ yearMonth: rowYearMonth ?? yearMonth, personId, personName }),
  });

  const deleteAction = (deviation: ApiModel.SalaryDeviationDescriptor, isAllowance: boolean): MoreActionMenuItem => ({
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.RemoveDeviation),
    icon: IconDefinitionConstants.WTrashAlt,
    hidden: !deviation.hasDeviationsForPeriod,
    actionDialog: {
      isOpen: false,
      title: isAllowance
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteAllowanceQuestion)
        : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteDeviationQuestion),
      titleIcon: IconDefinitionConstants.WTrashAlt,
      dialogType: DialogEnums.DialogType.Message,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
          onClick: onDelete.bind(null, deviation.id),
          type: StyleEnums.DisplayType.Danger,
          successMessage: isAllowance
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteAllowanceSuccess)
            : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteLeaveSuccess),
        },
      ],
      children: isAllowance
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteWarningAllowance)
        : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteWarningDeviation),
      type: StyleEnums.DisplayType.Danger,
    },
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess: () => reloadList && reloadList(),
  });

  const getRowActions = (row: ApiModel.SalaryDeviationDescriptor): MoreActionMenuItem[] => {
    if (!salaryDeviationsService.isMonthDisabled(deviationMonth?.state ?? row.monthState)) {
      if (row.hasDeviationsForPeriod) {
        return [
          addAllowanceAction(row.personId, row.personName, row.yearAndMonth),
          addLeaveAction(row.personId, row.personName, row.yearAndMonth),
          deleteAction(row, SalaryDeviationConstants.AllowanceTypes.includes(row.deviationType)),
        ];
      } else {
        return [addAllowanceAction(row.personId, row.personName), addLeaveAction(row.personId, row.personName)];
      }
    } else {
      return [];
    }
  };

  return { getRowActions };
}

export default useSalaryDeviationHoverMoreActions;
